import type { DictationBody, DictationResultDto, DictationSuggestionDto } from '@reos/field-intake';
import type { Room } from './intake-rooms.js';

/**
 * Krok NAB4 — DIKTAT KE SKUPINE NEBO MISTNOSTI v prohlizeci.
 *
 * Obrazovka drzi u KAZDEHO mikrofonu vlastni stav (`scopeKey`): nahrava se /
 * ceka na rozbor / navrhy / „teď to nejde". Rozbor dela server
 * (`POST /v1/field-intake/sessions/:id/dictation`) nad nahravkou, kterou uz
 * poslala offline fronta — bez signálu se diktat jen ulozi a navrhy dojdou po
 * pripojeni.
 *
 * NIC se nezapisuje bez potvrzeni: navrh „sviti" u bodu a makler ho potvrdi
 * klepnutim; zapis pak jde TOUTEZ cestou jako kazda odpoved checklistu.
 */

export type DictationStatus = 'recording' | 'pending' | 'ready' | 'unavailable';

export interface DictationState {
  readonly status: DictationStatus;
  /** Prepis diktatu — makler vidi, co REOS slysel. */
  readonly text: string;
  readonly suggestions: readonly DictationSuggestionDto[];
}

export type DictationStates = Readonly<Record<string, DictationState>>;

/** Strop delky diktatu (zadani: krátký diktát do 60 s). */
export const DICTATION_MAX_SECONDS = 60;

export function groupScopeKey(groupId: string): string {
  return `group:${groupId}`;
}

export function roomScopeKey(roomId: string): string {
  return `room:${roomId}`;
}

/** Telo pozadavku pro skupinu checklistu. */
export function groupDictationBody(recordingId: string, groupId: string): DictationBody {
  return {
    recordingId,
    scope: { kind: 'checklist_group', groupId, templateKind: 'byt-prodej', roomId: null, rooms: [] },
  };
}

/**
 * Telo pozadavku pro mistnost. Do omezeni jde CELY seznam mistnosti, protoze
 * jedna veta zmini vic mistnosti („v obýváku plovoucí, kuchyň dlažba") a navrh
 * ma dojit ke spravne; server seznam nema, zije ve stavu obrazovky.
 */
export function roomDictationBody(recordingId: string, roomId: string, rooms: readonly Room[]): DictationBody {
  return {
    recordingId,
    scope: {
      kind: 'room',
      roomId,
      rooms: rooms.map((room) => ({ id: String(room['id'] ?? ''), name: String(room['name'] ?? '') })),
      groupId: null,
      templateKind: 'byt-prodej',
    },
  };
}

/** Odpoved serveru -> stav mikrofonu. Bez prepisu je stav „teď to nejde". */
export function dictationStateOf(result: DictationResultDto): DictationState {
  if (!result.transcriptionAvailable || !result.extractionAvailable) {
    return { status: 'unavailable', text: result.text, suggestions: [] };
  }
  return { status: 'ready', text: result.text, suggestions: result.suggestions };
}

/** Navrh u bodu checklistu (`pointId`) — ve vsech otevrenych diktatech. */
export function suggestionForPoint(states: DictationStates, pointId: string): DictationSuggestionDto | null {
  for (const state of Object.values(states)) {
    const hit = state.suggestions.find((suggestion) => suggestion.kind === 'point' && suggestion.pointId === pointId);
    if (hit !== undefined) return hit;
  }
  return null;
}

/** Navrh u pole mistnosti — i z diktatu, ktery makler zacal u JINE mistnosti. */
export function suggestionForRoomField(
  states: DictationStates,
  roomId: string,
  fieldId: string,
): DictationSuggestionDto | null {
  for (const state of Object.values(states)) {
    const hit = state.suggestions.find(
      (suggestion) => suggestion.kind === 'room_field' && suggestion.roomId === roomId && suggestion.fieldId === fieldId,
    );
    if (hit !== undefined) return hit;
  }
  return null;
}

/** Potvrzeny (nebo zamitnuty) navrh ze stavu zmizi — jinak by svitil navzdy. */
export function withoutSuggestion(states: DictationStates, suggestion: DictationSuggestionDto): DictationStates {
  const out: Record<string, DictationState> = {};
  for (const [key, state] of Object.entries(states)) {
    out[key] = {
      ...state,
      suggestions: state.suggestions.filter(
        (candidate) =>
          !(
            candidate.kind === suggestion.kind &&
            candidate.pointId === suggestion.pointId &&
            candidate.roomId === suggestion.roomId &&
            candidate.fieldId === suggestion.fieldId
          ),
      ),
    };
  }
  return out;
}
